import React, { useCallback, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { FormattedMessage } from 'react-intl';
import { EXPIRING_SECONDS } from '../../hooks/useSessionExpiry';

interface SessionTimeoutDialogProps {
  open: boolean;
  onLogout?: () => void;
  onContinue?: () => void;
  secondsRemaining: number;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: theme.spacing(46)
  },
  seconds: {
    ...theme.typography.h5,
    padding: theme.spacing(1, 1, 1, 1)
  }
}));

export const SessionTimeoutDialog = ({
  open,
  onLogout,
  onContinue,
  secondsRemaining
}: SessionTimeoutDialogProps): JSX.Element => {
  const classes = useStyles();

  const handleLogout = useCallback(() => {
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const handleContinue = useCallback(() => {
    if (onContinue) {
      onContinue();
    }
  }, [onContinue]);

  const seconds = useMemo<string>(
    () =>
      (secondsRemaining > EXPIRING_SECONDS
        ? '...'
        : secondsRemaining
      ).toString(),
    [secondsRemaining]
  );

  return (
    <Dialog data-testid={'session-timeout-dialog'} open={open}>
      <DialogTitle>
        <FormattedMessage id="session.expiring" />
      </DialogTitle>
      <DialogContent className={classes.dialog}>
        <p>
          <FormattedMessage id="session.expiresIn" />
          <span data-testid={'seconds-remaining'} className={classes.seconds}>
            {seconds}
          </span>
          <FormattedMessage id="session.seconds" />
        </p>
        <p>
          <FormattedMessage id="session.continuePrompt" />
        </p>
      </DialogContent>
      <DialogActions>
        <Button data-testid={'logout'} onClick={handleLogout}>
          <FormattedMessage id="session.logout" />
        </Button>
        <Button
          data-testid={'continue'}
          color="primary"
          variant="contained"
          onClick={handleContinue}
        >
          <FormattedMessage id="session.continue" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
